import { httpsCallable } from 'firebase/functions';
import { databaseId, functions } from '@/lib/firebase';

export class InviteAlreadyClaimedError extends Error {
  constructor(cause?: unknown) {
    super('INVITE_ALREADY_CLAIMED', { cause });
    this.name = 'InviteAlreadyClaimedError';
  }
}

export const DEFAULT_MAX_USER_INVITE_CLAIMS = 10;

const createUserInviteCodeFn = httpsCallable<{ databaseId: string }, { inviteCode: string }>(
  functions,
  'createUserInviteCode'
);
const verifyInviteCodeFn = httpsCallable<{ databaseId: string; code: string }, { valid: true }>(
  functions,
  'verifyInviteCode'
);
const claimInviteCodeFn = httpsCallable<
  { databaseId: string; code: string },
  { success: true; type: 'general' | 'user' }
>(functions, 'claimInviteCode');

/** Ensure the current user's invite-code mapping is created server-side. */
export async function setUserInviteCode(): Promise<void> {
  await createUserInviteCodeFn({ databaseId });
}

/** Verify invite code via the backend callable function. */
export async function verifyInviteCode(code: string): Promise<boolean> {
  const trimmedCode = code.trim().toUpperCase();
  if (!trimmedCode) return false;

  try {
    await verifyInviteCodeFn({ databaseId, code: trimmedCode });
    return true;
  } catch (err) {
    const functionsError = err as { code?: string; message?: string };

    if (
      functionsError.code === 'functions/failed-precondition' &&
      functionsError.message === 'INVITE_ALREADY_CLAIMED'
    ) {
      throw new InviteAlreadyClaimedError(err);
    }

    return false;
  }
}

/** Mark invite code as claimed after successful registration. */
export async function claimInviteCode(code: string): Promise<{ type: 'general' | 'user' }> {
  const trimmedCode = code.trim().toUpperCase();

  try {
    const { data } = await claimInviteCodeFn({ databaseId, code: trimmedCode });
    if (typeof data !== 'object' || data === null) {
      throw new Error('Unexpected invite code response: invalid payload.');
    }
    const { type } = data as { type?: unknown };
    if (type !== 'general' && type !== 'user') {
      throw new Error('Unexpected invite code response: missing or invalid type.');
    }
    return { type };
  } catch (err) {
    const functionsError = err as { code?: string; message?: string };

    if (functionsError.code === 'functions/failed-precondition') {
      if (functionsError.message === 'INVITE_ALREADY_CLAIMED') {
        throw new InviteAlreadyClaimedError(err);
      }
      if (functionsError.message === 'INVITE_CODE_EXPIRED') {
        throw new Error('INVITE_CODE_EXPIRED', { cause: err });
      }
    }

    if (
      functionsError.code === 'functions/not-found' ||
      functionsError.code === 'functions/invalid-argument'
    ) {
      throw new Error('INVALID_INVITE_CODE', { cause: err });
    }

    throw err;
  }
}
